import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/rentApi";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  LayoutDashboard, Building2, FileText, Users, Wrench, Wallet,
  Calculator, FileSpreadsheet, LogOut, Home, Menu, X, CreditCard, ShieldCheck, FileSignature,
} from "lucide-react";
import { useState } from "react";

const NAV = {
  proprietaire: [
    ["", "Tableau de bord", LayoutDashboard],
    ["biens", "Biens", Building2],
    ["baux", "Baux", FileText],
    ["documents", "Documents", FileSignature],
    ["candidatures", "Candidatures", Users],
    ["maintenance", "Maintenance", Wrench],
    ["loyers", "Loyers", Wallet],
    ["comptabilite", "Comptabilité", Calculator],
    ["liasse", "Liasse fiscale", FileSpreadsheet],
    ["facturation", "Facturation", CreditCard],
  ],
  locataire: [
    ["", "Tableau de bord", LayoutDashboard],
    ["baux", "Mon bail", FileText],
    ["documents", "Documents", FileSignature],
    ["loyers", "Mes loyers", Wallet],
    ["maintenance", "Maintenance", Wrench],
  ],
  candidat: [
    ["", "Tableau de bord", LayoutDashboard],
    ["biens", "Annonces", Building2],
    ["candidatures", "Mes candidatures", Users],
  ],
  mainteneur: [
    ["", "Tableau de bord", LayoutDashboard],
    ["maintenance", "Interventions", Wrench],
  ],
  admin: [
    ["", "Tableau de bord", LayoutDashboard],
    ["biens", "Biens", Building2],
    ["baux", "Baux", FileText],
    ["documents", "Documents", FileSignature],
    ["candidatures", "Candidatures", Users],
    ["maintenance", "Maintenance", Wrench],
    ["loyers", "Loyers", Wallet],
    ["comptabilite", "Comptabilité", Calculator],
    ["liasse", "Liasse fiscale", FileSpreadsheet],
    ["facturation", "Facturation", CreditCard],
  ],
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = [...(NAV[user?.role] || NAV.candidat), ["donnees", "Mes données", ShieldCheck]];
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = () => {
    logout();
    navigate("/");
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-6 h-16 border-b border-border">
        <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground grid place-items-center">
          <Home className="w-4 h-4" />
        </div>
        <span className="font-heading font-bold text-xl">Rent</span>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map(([path, label, Icon]) => (
          <NavLink
            key={path}
            to={`/app/${path}`}
            end={path === ""}
            data-testid={`nav-${path || "dashboard"}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <Icon className="w-4 h-4 shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="w-9 h-9"><AvatarFallback className="bg-secondary text-xs font-semibold">{initials}</AvatarFallback></Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{ROLE_LABELS[user?.role]}</p>
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start gap-3 mt-1 text-muted-foreground" onClick={doLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Déconnexion
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:block w-64 border-r border-border bg-card fixed inset-y-0 left-0 z-40">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-card border-r border-border"><SidebarInner /></aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs px-3 py-1.5 rounded-full bg-accent text-accent-foreground font-medium" data-testid="role-badge">
              Espace {ROLE_LABELS[user?.role]}
            </span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
